import React, { useState, useEffect } from 'react';
import { useLanguage } from '../i18n';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import {
  listDocuments,
  getDocument,
  approveDailyFee,
  rejectDailyFee,
} from '../services/api';
import { DocumentReview as DocumentReviewType } from '../types';

const DocumentReviewPage: React.FC = () => {
  const { t } = useLanguage();
  const [documents, setDocuments] = useState<DocumentReviewType[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedDocument, setSelectedDocument] = useState<DocumentReviewType | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    loadDocuments();
  }, [statusFilter]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await listDocuments(statusFilter || undefined);
      setDocuments(data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (documentId: string) => {
    try {
      const doc = await getDocument(documentId);
      setSelectedDocument(doc);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Failed to load document details:', error);
    }
  };

  const handleApprove = async (documentId: string) => {
    try {
      await approveDailyFee(documentId);
      loadDocuments();
      if (detailDialogOpen) {
        setDetailDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to approve:', error);
    }
  };

  const handleReject = async () => {
    if (!selectedDocument || !rejectReason) return;
    try {
      await rejectDailyFee(selectedDocument.document_id, rejectReason);
      setRejectDialogOpen(false);
      setRejectReason('');
      loadDocuments();
      if (detailDialogOpen) {
        setDetailDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to reject:', error);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t.documentReviewPage.title}</Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{t.documentReviewPage.filterByStatus}</InputLabel>
          <Select
            value={statusFilter}
            label={t.documentReviewPage.filterByStatus}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">{t.documentReviewPage.all}</MenuItem>
            <MenuItem value="pending">{t.common.pending}</MenuItem>
            <MenuItem value="processed">{t.common.processing}</MenuItem>
            <MenuItem value="approved">{t.common.approve}</MenuItem>
            <MenuItem value="rejected">{t.common.reject}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={40} thickness={4} sx={{ color: '#6366F1' }} />
        </Box>
      ) : documents.length === 0 ? (
        <Alert severity="info">{t.documentReviewPage.noDocuments}</Alert>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t.documentReviewPage.documentId}</TableCell>
                <TableCell>{t.documentReviewPage.filename}</TableCell>
                <TableCell>{t.documentReviewPage.deliveryNumber}</TableCell>
                <TableCell>{t.documentReviewPage.status}</TableCell>
                <TableCell>{t.documentReviewPage.uploadedAt}</TableCell>
                <TableCell>{t.documentReviewPage.classification}</TableCell>
                <TableCell>{t.exceptionQueuePage.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.document_id}>
                  <TableCell>{doc.document_id.substring(0, 8)}...</TableCell>
                  <TableCell>{doc.filename}</TableCell>
                  <TableCell>{doc.delivery_number || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={doc.status}
                      color={
                        doc.status === 'approved'
                          ? 'success'
                          : doc.status === 'rejected'
                          ? 'error'
                          : doc.status === 'processed'
                          ? 'info'
                          : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(doc.uploaded_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {doc.classification ? (
                      <Chip
                        label={doc.classification.is_exception ? t.documentReviewPage.exception : t.documentReviewPage.standard}
                        color={doc.classification.is_exception ? 'warning' : 'success'}
                        size="small"
                      />
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleViewDetails(doc.document_id)}
                    >
                      {t.common.view}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t.documentReviewPage.documentDetails}</DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t.documentReviewPage.basicInfo}
              </Typography>
              <Typography variant="body2">
                <strong>{t.documentReviewPage.documentId}:</strong> {selectedDocument.document_id}
              </Typography>
              <Typography variant="body2">
                <strong>{t.documentReviewPage.filename}:</strong> {selectedDocument.filename}
              </Typography>
              <Typography variant="body2">
                <strong>{t.documentReviewPage.deliveryNumber}:</strong>{' '}
                {selectedDocument.delivery_number || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>{t.documentReviewPage.status}:</strong> {selectedDocument.status}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                {t.documentReviewPage.extractedData}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {Object.entries(selectedDocument.extracted_data).map(
                      ([key, value]) =>
                        value && (
                          <TableRow key={key}>
                            <TableCell>
                              <strong>
                                {key
                                  .replace('_', ' ')
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                              </strong>
                            </TableCell>
                            <TableCell>{value}</TableCell>
                          </TableRow>
                        )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {selectedDocument.comparison_result && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    {t.documentReviewPage.comparisonResults}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t.exceptionQueuePage.field}</TableCell>
                          <TableCell>{t.exceptionQueuePage.document}</TableCell>
                          <TableCell>{t.exceptionQueuePage.erp}</TableCell>
                          <TableCell>{t.exceptionQueuePage.match}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedDocument.comparison_result.map((result) => (
                          <TableRow key={result.field}>
                            <TableCell>
                              {result.field
                                .replace('_', ' ')
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </TableCell>
                            <TableCell>{result.document_value || 'N/A'}</TableCell>
                            <TableCell>{result.erp_value || 'N/A'}</TableCell>
                            <TableCell>
                              {result.match ? (
                                <Chip label="✓" color="success" size="small" />
                              ) : (
                                <Chip label="✗" color="error" size="small" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {selectedDocument.classification && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    {t.documentReviewPage.classification}
                  </Typography>
                  <Alert
                    severity={
                      selectedDocument.classification.is_exception
                        ? 'warning'
                        : 'success'
                    }
                  >
                    {selectedDocument.classification.is_exception
                      ? t.documentReviewPage.exceptionCase
                      : t.documentReviewPage.standardCase}
                    {selectedDocument.classification.exception_reasons.length > 0 && (
                      <ul>
                        {selectedDocument.classification.exception_reasons.map(
                          (reason, idx) => (
                            <li key={idx}>{reason}</li>
                          )
                        )}
                      </ul>
                    )}
                  </Alert>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>{t.common.close}</Button>
          {selectedDocument &&
            selectedDocument.status === 'processed' &&
            !selectedDocument.classification?.is_exception && (
              <>
                <Button
                  onClick={() => {
                    setRejectDialogOpen(true);
                  }}
                  color="error"
                >
                  {t.common.reject}
                </Button>
                <Button
                  onClick={() => handleApprove(selectedDocument.document_id)}
                  color="primary"
                  variant="contained"
                >
                  {t.common.approve}
                </Button>
              </>
            )}
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>{t.documentReviewPage.rejectDailyFee}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t.documentReviewPage.rejectionReason}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            rows={4}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>{t.common.cancel}</Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={!rejectReason}
          >
            {t.common.reject}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DocumentReviewPage;

